// Auth state via react-query. The session lives in an HttpOnly cookie; the
// client only knows { authenticated, email, tier } from /api/auth/me.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import type { AuthState } from "./types";

export function useAuth() {
  return useQuery<AuthState>({
    queryKey: ["me"],
    queryFn: () => api.get<AuthState>("/api/auth/me"),
    staleTime: 5 * 60 * 1000,
  });
}

export function useRequestCode() {
  return useMutation({
    mutationFn: (email: string) => api.post("/api/auth/request-code", { email }),
  });
}

export function useVerifyCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, code }: { email: string; code: string }) =>
      api.post<AuthState>("/api/auth/verify", { email, code }),
    onSuccess: (data) => {
      qc.setQueryData(["me"], { ...data, authenticated: true });
    },
  });
}

export function useSignOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/api/auth/logout"),
    onSuccess: () => {
      qc.setQueryData(["me"], { authenticated: false });
      qc.clear();
    },
  });
}
